import { getAuthToken } from '@/lib/auth-token';

const TIMEOUT_MS = 10_000;

function getBaseUrl(): string {
  return import.meta.env.VITE_API_BASE_URL || '/api/v1';
}

export class BetaApiError extends Error {
  constructor(
    public statusCode: number,
    message: string,
    public details?: unknown,
  ) {
    super(message);
    this.name = 'BetaApiError';
  }
}

export interface BetaFetchOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
}

/**
 * Beta-tree fetch primitive. Mirrors `src/services/api.ts` but lives in
 * its own module so the beta tree has no import-time dependency on the
 * legacy app. Auth token resolution is delegated to the shared helper.
 */
export async function betaFetch<T = unknown>(
  endpoint: string,
  opts: BetaFetchOptions = {},
): Promise<T> {
  const { body, signal, headers, ...rest } = opts;

  // If caller provided a signal, honor it. Otherwise wire up our own timeout.
  const timeoutController = signal ? null : new AbortController();
  const effectiveSignal = signal ?? timeoutController?.signal;
  const timeoutId = timeoutController
    ? setTimeout(() => timeoutController.abort(), TIMEOUT_MS)
    : null;

  try {
    const token = await getAuthToken();
    const response = await fetch(`${getBaseUrl()}${endpoint}`, {
      ...rest,
      signal: effectiveSignal,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(headers as Record<string, string> | undefined),
      },
      body: body == null ? undefined : JSON.stringify(body),
    });

    if (!response.ok) {
      let details: unknown = undefined;
      try { details = await response.json(); } catch { /* not JSON */ }
      throw new BetaApiError(response.status, `Beta API ${response.status}`, details);
    }

    // 204: no content
    if (response.status === 204) return undefined as T;

    return (await response.json()) as T;
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }
}

/** Alias for `betaFetch` — kept for backwards-compat with test spies that
 *  import `api` from this module. New code should prefer `betaFetch`. */
export const api = betaFetch;
